import { useEffect, useRef } from 'react';
import { BulkCheckResult } from '../helpers/apiHelpers';
import { apiFetchBusinessData } from '../helpers/apiHelpers';
import { buildLabelsIndex } from '../helpers/labels';
import { useStableAccessToken } from './useStableAccessToken';

/**
 * Custom hook that detects when apps fall back to a different branch (e.g., default branch)
 * and fetches labels for those apps using the actual branch that was used.
 * 
 * This fixes the issue where apps show "No label detected" when they fall back to master,
 * even though they have labels on master.
 */
export function useBranchFallbackLabels(params: {
  bulkResults: BulkCheckResult[];
  applicationBranch: string;
  deploymentBranch: string;
  onLabelsUpdate: (updates: Record<string, { found: { name: string; value: string }[]; domainValue: string | null }>) => void;
}) {
  const { bulkResults, applicationBranch, deploymentBranch, onLabelsUpdate } = params;
  const tokenGetter = useStableAccessToken();
  const processedAppsRef = useRef<Set<string>>(new Set());

  // Clear processed apps when applicationBranch changes
  useEffect(() => {
    processedAppsRef.current.clear();
  }, [applicationBranch]);

  useEffect(() => {
    if (!bulkResults.length || !applicationBranch) return;

    // Find apps where the backend used a different branch than applicationBranch
    // This indicates a fallback to default branch happened
    const appsNeedingLabelRefresh = bulkResults
      .filter(res => {
        // If the branch in results differs from applicationBranch, it means fallback happened
        // Only process if we haven't already processed this app+branch combination
        const key = `${res.application}:${res.branch}`;
        if (processedAppsRef.current.has(key)) {
          return false;
        }
        return res.branch && res.branch !== applicationBranch;
      })
      .map(res => {
        const key = `${res.application}:${res.branch}`;
        processedAppsRef.current.add(key);
        return { app: res.application, branch: res.branch! };
      });

    if (appsNeedingLabelRefresh.length === 0) return;

    // Fetch labels for each app using its actual branch
    (async () => {
      const labelUpdates: Record<string, { found: { name: string; value: string }[]; domainValue: string | null }> = {};

      // Group by branch to minimize API calls
      const branchGroups = new Map<string, string[]>();
      appsNeedingLabelRefresh.forEach(({ app, branch }) => {
        if (!branchGroups.has(branch)) {
          branchGroups.set(branch, []);
        }
        branchGroups.get(branch)!.push(app);
      });

      // Fetch labels for each unique branch
      for (const [branch, apps] of branchGroups.entries()) {
        try {
          const branchData = await apiFetchBusinessData(branch, tokenGetter, deploymentBranch);
          const newLabels = buildLabelsIndex(branchData);

          // Update labels for all apps on this branch
          apps.forEach(app => {
            labelUpdates[app] = newLabels[app] || { found: [], domainValue: null };
          });
        } catch (labelError) {
          console.warn('Error fetching labels for branch', branch, labelError);
          // If labels fetch fails, clear labels for these apps
          apps.forEach(app => {
            labelUpdates[app] = { found: [], domainValue: null };
          });
        }
      }

      // Notify parent component to update labels
      if (Object.keys(labelUpdates).length > 0) {
        onLabelsUpdate(labelUpdates);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkResults, applicationBranch, deploymentBranch, onLabelsUpdate]); // tokenGetter is stable, don't include in deps
}

